import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import {
  ConfirmDialog,
  ContentHeader,
  Field,
  Modal,
  MoneyInput,
  PercentInput,
  QueryStatus,
  useToast,
} from "@cx/ui";
import type { FinancialAccountView, PaymentMethodFormValues, PaymentMethodView } from "@cx/contracts";
import { SEFAZ_PAYMENT_TYPES, formatBRL, formatPct, type SefazPaymentType } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { SEFAZ_LABEL } from "./labels";

type Values = {
  name: string;
  sefazType: SefazPaymentType;
  feePct4: number;
  feeFixedCents: number;
  settlementDays: number;
  defaultAccountId: string | null;
  active: boolean;
};
const EMPTY: Values = {
  name: "",
  sefazType: "01",
  feePct4: 0,
  feeFixedCents: 0,
  settlementDays: 0,
  defaultAccountId: null,
  active: true,
};

/** Formas de pagamento (RF-FIN-07): meio SEFAZ, taxa da operadora, prazo de recebimento e conta padrão. */
export function PaymentMethodsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const key = ["payment-methods", companyId];
  const list = useQuery({
    queryKey: key,
    queryFn: () => api<{ data: PaymentMethodView[] }>("/payment-methods"),
  });
  const [editing, setEditing] = useState<{ id?: string; values: Values } | null>(null);
  const [deleting, setDeleting] = useState<PaymentMethodView | null>(null);
  const changed = () => qc.invalidateQueries({ queryKey: key });
  const del = useMutation({
    mutationFn: (id: string) => api(`/payment-methods/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Forma de pagamento excluída");
      changed();
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });

  return (
    <>
      <ContentHeader
        title="Formas de pagamento"
        breadcrumb={["Cadastros", "Formas de pagamento"]}
        actions={
          can(perms, "payment_methods", "create") && (
            <button type="button" className="btn btn-primary" onClick={() => setEditing({ values: EMPTY })}>
              Incluir forma de pagamento
            </button>
          )
        }
      />
      <div className="box card">
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && list.data.data.length === 0 && (
          <p className="text-muted p-3 mb-0">
            Nenhuma forma de pagamento. Cadastre dinheiro, Pix, cartão e boleto com as taxas da operadora.
          </p>
        )}
        {list.isSuccess && list.data.data.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Formas de pagamento">
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Meio (SEFAZ)</th>
                  <th scope="col" className="text-end">
                    Taxa
                  </th>
                  <th scope="col" className="text-end">
                    Prazo
                  </th>
                  <th scope="col">Conta padrão</th>
                  <th scope="col">Situação</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((m) => (
                  <tr key={m.id}>
                    <td>{m.name}</td>
                    <td>{SEFAZ_LABEL[m.sefazType]}</td>
                    <td className="text-end">
                      {formatPct(m.feePct4)}
                      {m.feeFixedCents > 0 && ` + ${formatBRL(m.feeFixedCents)}`}
                    </td>
                    <td className="text-end">{m.settlementDays} d</td>
                    <td>{m.defaultAccountName ?? "—"}</td>
                    <td>{m.active ? "Ativa" : "Inativa"}</td>
                    <td className="text-end text-nowrap">
                      {can(perms, "payment_methods", "update") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-2"
                          aria-label={`Editar ${m.name}`}
                          onClick={() => setEditing({ id: m.id, values: { ...m } })}
                        >
                          Editar
                        </button>
                      )}
                      {can(perms, "payment_methods", "delete") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Excluir ${m.name}`}
                          disabled={del.isPending}
                          onClick={() => setDeleting(m)}
                        >
                          Excluir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing && (
        <PaymentMethodDialog
          id={editing.id}
          initial={editing.values}
          onClose={() => setEditing(null)}
          onSaved={changed}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Excluir forma de pagamento"
        message={`Excluir a forma de pagamento "${deleting?.name ?? ""}"? Ela pode ser restaurada pela Consulta de exclusões.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          if (deleting) del.mutate(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

function PaymentMethodDialog({
  id,
  initial,
  onClose,
  onSaved,
}: {
  id?: string;
  initial: Values;
  onClose(): void;
  onSaved(): void;
}) {
  const { companyId, api } = useSession();
  const toast = useToast();
  const [v, setV] = useState(initial);
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const accounts = useQuery({
    queryKey: ["fin-accounts", companyId],
    queryFn: () => api<{ data: FinancialAccountView[] }>("/financial-accounts"),
  });
  const save = useMutation({
    mutationFn: () => {
      const body: PaymentMethodFormValues = v;
      return api(id ? `/payment-methods/${id}` : "/payment-methods", {
        method: id ? "PUT" : "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      toast("success", "Forma de pagamento salva");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "duplicate_payment_method")
        setErrors({ name: e.message });
      toast("danger", errorMessage(e));
    },
  });
  return (
    <Modal
      open={open}
      title={id ? "Editar forma de pagamento" : "Nova forma de pagamento"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Nome" required className="col-12" error={errors.name}>
          <input
            value={v.name}
            maxLength={60}
            data-autofocus
            onChange={(e) => setV({ ...v, name: e.target.value })}
          />
        </Field>
        <Field label="Meio de pagamento (SEFAZ)" required className="col-md-6" error={errors.sefazType}>
          <select
            className="form-select"
            value={v.sefazType}
            onChange={(e) => setV({ ...v, sefazType: e.target.value as SefazPaymentType })}
          >
            {SEFAZ_PAYMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t} · {SEFAZ_LABEL[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Conta padrão" className="col-md-6" error={errors.defaultAccountId}>
          <select
            className="form-select"
            value={v.defaultAccountId ?? ""}
            onChange={(e) => setV({ ...v, defaultAccountId: e.target.value || null })}
          >
            <option value="">Nenhuma</option>
            {accounts.data?.data
              .filter((a) => a.active || a.id === v.defaultAccountId)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Taxa (%)" className="col-md-4" error={errors.feePct4} hint="Sobre o valor recebido">
          <PercentInput value={v.feePct4} onChange={(p) => setV({ ...v, feePct4: p ?? 0 })} />
        </Field>
        <Field label="Taxa fixa" className="col-md-4" error={errors.feeFixedCents}>
          <MoneyInput value={v.feeFixedCents} onChange={(c) => setV({ ...v, feeFixedCents: c ?? 0 })} />
        </Field>
        <Field label="Prazo de recebimento (dias)" className="col-md-4" error={errors.settlementDays}>
          <input
            type="number"
            min={0}
            max={365}
            value={v.settlementDays}
            onChange={(e) => setV({ ...v, settlementDays: Number(e.target.value) || 0 })}
          />
        </Field>
      </div>
      <div className="form-check">
        <input
          id="pm-active"
          type="checkbox"
          className="form-check-input"
          checked={v.active}
          onChange={(e) => setV({ ...v, active: e.target.checked })}
        />
        <label className="form-check-label" htmlFor="pm-active">
          Ativa
        </label>
      </div>
    </Modal>
  );
}
